<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-message-merging — the strip above the composer while an agent's
// WhatsApp texts are held to be merged into one message.
//
// There is deliberately no "send now" button (Cris, 2026-09-26): pressing
// Enter — or the send button — with an EMPTY composer sends the held texts
// now. The strip only hints at it. Deshacer (or Esc) takes the last held text
// back into the composer.
//
// Paused (the customer replied mid-hold): nothing is sent on its own. The strip
// asks "¿Enviar igual?"; Enter on an empty composer sends, Deshacer brings the
// whole merged text back. A closing 24 h window only turns into a warning.
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  group: { type: Object, required: true },
  composerHasText: { type: Boolean, default: false },
});

const emit = defineEmits(['undo']);
const { t } = useI18n();

const now = ref(Date.now());
let ticker = null;
onMounted(() => {
  ticker = setInterval(() => {
    now.value = Date.now();
  }, 250);
});
onUnmounted(() => clearInterval(ticker));

const isPaused = computed(() => !!props.group.paused);
const remainingSeconds = computed(() =>
  Math.max(0, Math.ceil((props.group.deadlineAt - now.value) / 1000))
);
const capSeconds = computed(() =>
  Math.max(0, Math.ceil((props.group.capAt - now.value) / 1000))
);
const isWaitingForTyping = computed(
  () => props.composerHasText && now.value - props.group.lastActivityAt < 1500
);
const progress = computed(() => {
  if (isPaused.value) return 100;
  const span = props.group.deadlineAt - props.group.lastActivityAt;
  if (span <= 0) return 0;
  const left = (props.group.deadlineAt - now.value) / span;
  return Math.min(100, Math.max(0, left * 100));
});

const title = computed(() => {
  if (isPaused.value) return t('KAPTION_MESSAGE_MERGE.STRIP.PAUSED_TITLE');
  return props.group.count > 1
    ? t('KAPTION_MESSAGE_MERGE.STRIP.TITLE_MANY', {
        count: props.group.count,
      })
    : t('KAPTION_MESSAGE_MERGE.STRIP.TITLE_ONE');
});
const subtitle = computed(() => {
  if (isPaused.value) {
    if (props.group.windowWarning) {
      return t('KAPTION_MESSAGE_MERGE.STRIP.PAUSED_WINDOW');
    }
    return props.group.count > 1
      ? t('KAPTION_MESSAGE_MERGE.STRIP.PAUSED_SUBTITLE_MANY', {
          count: props.group.count,
        })
      : t('KAPTION_MESSAGE_MERGE.STRIP.PAUSED_SUBTITLE_ONE');
  }
  return isWaitingForTyping.value
    ? t('KAPTION_MESSAGE_MERGE.STRIP.WAITING', { seconds: capSeconds.value })
    : t('KAPTION_MESSAGE_MERGE.STRIP.COUNTDOWN', {
        seconds: remainingSeconds.value,
      });
});
</script>

<template>
  <div
    class="border-b border-n-weak"
    :class="isPaused ? 'bg-n-blue-2' : 'bg-n-amber-2'"
    role="status"
    aria-live="polite"
    data-testid="kaption-pending-send-strip"
    :data-paused="isPaused"
  >
    <div class="flex items-center gap-3 px-3 py-2">
      <span
        class="size-4 shrink-0"
        :class="
          isPaused
            ? 'i-lucide-message-circle-reply text-n-blue-11'
            : 'i-lucide-clock text-n-amber-11'
        "
      />
      <div class="flex min-w-0 flex-col">
        <span
          class="text-sm font-semibold"
          :class="isPaused ? 'text-n-blue-11' : 'text-n-amber-11'"
          data-testid="kaption-pending-send-title"
        >
          {{ title }}
        </span>
        <span
          class="truncate text-xs"
          :class="
            isPaused && group.windowWarning
              ? 'font-medium text-n-ruby-11'
              : 'text-n-slate-11'
          "
          data-testid="kaption-pending-send-subtitle"
        >
          {{ subtitle }}
        </span>
      </div>
      <span
        class="ltr:ml-auto rtl:mr-auto hidden items-center gap-1 whitespace-nowrap text-xs text-n-slate-11 sm:flex"
        data-testid="kaption-pending-send-hint"
      >
        <kbd
          class="rounded border border-n-weak bg-n-solid-1 px-1 font-sans text-[11px] text-n-slate-12"
        >
          {{ t('KAPTION_MESSAGE_MERGE.STRIP.ENTER_KEY') }}
        </kbd>
        {{
          isPaused
            ? t('KAPTION_MESSAGE_MERGE.STRIP.PAUSED_ENTER_HINT')
            : t('KAPTION_MESSAGE_MERGE.STRIP.ENTER_HINT')
        }}
      </span>
      <button
        type="button"
        class="shrink-0 rounded-full border border-n-weak bg-n-solid-1 px-3 py-1 text-xs font-semibold text-n-slate-12 hover:bg-n-alpha-2 sm:ltr:ml-0 ltr:ml-auto rtl:mr-auto"
        :title="
          isPaused
            ? t('KAPTION_MESSAGE_MERGE.STRIP.PAUSED_UNDO_TITLE')
            : t('KAPTION_MESSAGE_MERGE.STRIP.UNDO_TITLE')
        "
        data-testid="kaption-pending-send-undo"
        @click="emit('undo')"
      >
        {{ t('KAPTION_MESSAGE_MERGE.STRIP.UNDO') }}
      </button>
    </div>
    <div class="h-0.5 bg-n-alpha-2">
      <div
        class="h-0.5 transition-[width] duration-200 ease-linear"
        :class="isPaused ? 'bg-n-blue-9' : 'bg-n-amber-10'"
        :style="{ width: `${progress}%` }"
      />
    </div>
  </div>
</template>
